import { useI18n } from '@i18n';
import { Box, Stack } from '@mui/material';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import xor from 'lodash/xor';
import { useCallback, useRef, useState } from 'react';
import SearchBoxWithLazyLoading, { SearchItemType } from '@/components/search/SearchBoxWithLazyLoading';
import { useSession } from '@/components/session/useSession';
import usePosthogTracking from '@/hooks/usePosthogTracking';
import {
  ALL_ECONOMIC_UNITS_ITEM,
  useEconomicUnitsSelector,
} from '@/sections/StatusQuoAnalysis/NavigationSelectors/Hooks/useEconomicUnitsSelector';
import NavigationSelectorButton from '@/sections/StatusQuoAnalysis/NavigationSelectors/NavigationSelectorButton';

export const ALL_ECONOMIC_UNITS = 'allEconomicUnits';
const ECONOMIC_UNITS_QUERY_LIMIT = 10;

export default function EconomicUnitsSelector() {
  const { t } = useI18n();
  const { trackEvent } = usePosthogTracking();
  const {
    org: { orgSector },
  } = useSession();

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const economicUnitsSelectorButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isEconomicUnitSelectorOpen, setIsEconomicUnitSelectorOpen] = useState(false);

  const {
    economicUnits,
    loadedEconomicUnits,
    economicUnitIds,
    setEconomicUnitsIds,
    loading,
    loadingByIds,
    hasMore,
    refetch,
    loadMore,
    selectedItems,
    queryParams,
    setQueryParams,
  } = useEconomicUnitsSelector(debouncedSearchTerm, ECONOMIC_UNITS_QUERY_LIMIT);

  const onEconomicUnitSelected = (selectedEconomicUnit: SearchItemType) => {
    trackEvent('PORTFOLIO_SELECTED', {
      economic_unit_id: selectedEconomicUnit.value,
    });

    setEconomicUnitsIds((prev) => {
      if (selectedEconomicUnit.value === ALL_ECONOMIC_UNITS_ITEM().value) {
        return prev.includes(ALL_ECONOMIC_UNITS_ITEM().value) ? [] : [ALL_ECONOMIC_UNITS_ITEM().value];
      }
      return xor(
        prev.filter((p) => p !== ALL_ECONOMIC_UNITS_ITEM().value),
        [selectedEconomicUnit.value],
      );
    });
  };

  const count = queryParams?.economicUnitIds?.length ?? 0;
  const ecoUnit = loadedEconomicUnits.find((ecoUnit) => ecoUnit.value === queryParams?.economicUnitIds?.[0]);

  const selectorLabel =
    count > 1
      ? `${count} ${t('General_EconomicUnit', {
          count: count,
          orgSector,
        })}`
      : (ecoUnit?.label ?? '');

  const handleClickOpenSearchBox = useCallback(() => {
    setIsEconomicUnitSelectorOpen((prev) => !prev);
  }, []);

  const handleClear = () => {
    void setQueryParams({
      economicUnitIds: null,
      buildingIds: null,
    });

    setIsEconomicUnitSelectorOpen(false);

    trackEvent('STATUS_QUO_ECONOMIC_UNIT_SELECTED', {
      economic_unit_id: ALL_ECONOMIC_UNITS,
    });
  };

  const isLoading = loadingByIds && !loadedEconomicUnits.length;

  return (
    <Stack direction="row" alignItems={'center'} gap={1}>
      <Box>
        <NavigationSelectorButton
          ref={economicUnitsSelectorButtonRef}
          title={selectorLabel}
          onClick={handleClickOpenSearchBox}
          isSelected={isEconomicUnitSelectorOpen}
          tooltipText={t('General_SelectEconomicUnit', { orgSector })}
          isLoading={isLoading}
        />
        <SearchBoxWithLazyLoading
          isOpen={isEconomicUnitSelectorOpen}
          anchorRef={economicUnitsSelectorButtonRef}
          refetch={refetch}
          onItemSelected={onEconomicUnitSelected}
          selectedItems={selectedItems}
          isLoading={loading}
          listItems={economicUnits}
          loadMore={loadMore}
          hasMore={hasMore}
          limit={ECONOMIC_UNITS_QUERY_LIMIT}
          setHasMore={() => {}} //  Do not want to change the SearchBoxWithLazyLoading API
          setDebouncedSearchTerm={setDebouncedSearchTerm}
          debouncedSearchTerm={debouncedSearchTerm}
          noDataText={t('StatusQuoAnalysisFilterBarEconomicUnits_NoEconomicUnitsFound', { orgSector })}
          placeholder={t('General_SearchEconomicUnitPlaceholder', { orgSector })}
          handleClear={handleClear}
          isMultiSelect
          showClearButton={count > 0 || selectedItems.length > 0}
          onHideSearchBox={() => {
            setIsEconomicUnitSelectorOpen(false);
            const filteredArray = queryParams?.economicUnitIds ?? [];
            setEconomicUnitsIds(filteredArray);
          }}
          handleClick={() => {
            void setQueryParams({ economicUnitIds });
            setIsEconomicUnitSelectorOpen(false);
          }}
          disableApplyButton={
            isEqual(sortBy(economicUnitIds), sortBy(queryParams?.economicUnitIds ?? [])) || selectedItems.length === 0
          }
        />
      </Box>
    </Stack>
  );
}
